import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TrpcService } from 'src/app/services/trpc/trpc.service';

@Component({
  selector: 'app-import-summary-files-list',
  templateUrl: './import-summary-files-list.component.html',
  styleUrl: './import-summary-files-list.component.scss'
})
export class ImportSummaryFilesListComponent implements OnInit, OnChanges {

  @Input() date!: string;

  isListLoading: boolean = false;
  dailySummaryFiles: any[] = [];
  selectedDateString: string = ''; // YYYY-MM-DD format for HTML5 date input
  errorMessage: string | null = null;
  hasError: boolean = false;

  constructor(private trpcService: TrpcService, private messageService: MessageService,) { }

  ngOnInit(): void {
    console.log('ImportSummaryFilesListComponent initialized');
    console.log('Initial date input:', this.date);

    // Initialize selectedDateString from the input parameter if provided
    if (this.date) {
      // Convert MM/DD/YYYY to YYYY-MM-DD
      this.selectedDateString = this.convertMMDDYYYYToYYYYMMDD(this.date);
      console.log('Converted selectedDateString:', this.selectedDateString);
      this.loadDailySummaryFiles();
    } else {
      // If no date provided, default to today
      this.selectedDateString = this.formatDateToYYYYMMDD(new Date());
      console.log('No date provided, defaulting to today:', this.selectedDateString);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges triggered:', changes);
    this.dailySummaryFiles = [];

    if(changes['date'] && changes['date'].currentValue) {
      // Convert MM/DD/YYYY to YYYY-MM-DD for the HTML5 date input
      this.selectedDateString = this.convertMMDDYYYYToYYYYMMDD(changes['date'].currentValue);
      console.log('Updated selectedDateString from input:', this.selectedDateString);
      this.loadDailySummaryFiles();
    }
  }
  
  async loadDailySummaryFiles() {
    console.log('loadDailySummaryFiles called');
    console.log('selectedDateString value:', this.selectedDateString);

    // Clear previous error state
    this.errorMessage = null;
    this.hasError = false;
    this.messageService.clear();

    // Validate that we have a date to load
    if (!this.selectedDateString) {
      console.log('No date selected, showing error');
      this.hasError = true;
      this.errorMessage = "Please select a date.";
      return;
    }

    try {
      this.isListLoading = true;

      // Convert YYYY-MM-DD to MM/DD/YYYY format for the API
      const dateString = this.convertYYYYMMDDToMMDDYYYY(this.selectedDateString);
      console.log('Formatted date string for API:', dateString);

      let dailySummaryFilesResult: any = await this.trpcService.trpc.lots.getDailySummaryFiles.query({ date: dateString }).catch((error: any) => {
        console.error('API error:', error);
        this.hasError = true;
        this.errorMessage = error.message || "An error occurred while loading daily summary files.";
        throw error; // Re-throw to be caught by outer catch
      });

      console.log('API result:', dailySummaryFilesResult);

      if (dailySummaryFilesResult) {
        this.dailySummaryFiles = dailySummaryFilesResult;
        this.hasError = false; // Successfully loaded (even if empty results)
        console.log('Successfully loaded files:', this.dailySummaryFiles.length);
      }
    } catch (error: any) {
      console.error('Error in loadDailySummaryFiles:', error);
      this.hasError = true;
      if (!this.errorMessage) {
        this.errorMessage = "Unable to load daily summary files.";
      }
    } finally {
      this.isListLoading = false;
    }
  }

  onLoadButtonClick() {
    console.log('Load button clicked!');
    console.log('Current selectedDateString:', this.selectedDateString);
    this.loadDailySummaryFiles();
  }

  // Helper method to convert MM/DD/YYYY to YYYY-MM-DD
  private convertMMDDYYYYToYYYYMMDD(dateString: string): string {
    try {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
    } catch (error) {
      console.error('Error converting MM/DD/YYYY to YYYY-MM-DD:', error);
    }
    return '';
  }

  // Helper method to convert YYYY-MM-DD to MM/DD/YYYY
  private convertYYYYMMDDToMMDDYYYY(dateString: string): string {
    try {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    } catch (error) {
      console.error('Error converting YYYY-MM-DD to MM/DD/YYYY:', error);
    }
    return '';
  }

  // Helper method to format Date object to YYYY-MM-DD string (for HTML5 date input)
  private formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  downloadFile(fileContent: string, fileName: string) {
    
    this.messageService.clear();

    try {

      // Trigger file download
      const link = document.createElement('a');
      link.href = fileContent; // Full data URI (includes mime type)
      link.download = fileName; // e.g. "report.xlsx"
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // ✅ Optional: release memory
      URL.revokeObjectURL(link.href);

      this.messageService.add({
        severity: "success",
        detail: "File downloaded.",
      });

    } catch (error) {
      this.messageService.add({
        severity: "error",
        detail: "Unable to download daily summary file.",
      });
    }
  }
}
